import { Checkmark, Edit } from '@ui/components/icons';
import { Checkbox } from '@ui/lib/park/ui/checkbox';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { shortenAddress } from '@ui/utils/formatAddress';
import { getAddressGradient } from '@ui/utils/getAddressGradient';
import { useEffect, useRef, useState } from 'react';
import { HStack, HstackProps, styled, VStack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

const IDENTITY_STORAGE_KEY = 'share-identity-v1';

/**
 * Identity (avatar + display name) shown on share images. Shared by every share
 * overlay so a user configures it once. Avatar uploads are compressed to a small
 * JPEG and persisted with the name + toggle in localStorage.
 */
export const useShareIdentity = () => {
  const { address } = useAccount();
  const fileRef = useRef<HTMLInputElement | null>(null);
  // null = use the connected address as the display name (not spoofable).
  const [customName, setCustomName] = useState<string | null>(null);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [showIdentity, setShowIdentity] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  const defaultName = address
    ? shortenAddress({ address, leadingChars: 6, tailingChars: 4 })
    : '';
  const displayName =
    customName && customName.trim() ? customName : defaultName;

  // Load persisted identity once.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(IDENTITY_STORAGE_KEY);
      if (raw) {
        const j = JSON.parse(raw);
        if (typeof j.customName === 'string') setCustomName(j.customName);
        if (typeof j.avatar === 'string') setAvatar(j.avatar);
        if (typeof j.showIdentity === 'boolean')
          setShowIdentity(j.showIdentity);
      }
    } catch {}
    setHydrated(true);
  }, []);

  // Persist identity (avatar is already compressed to a small JPEG dataURL).
  useEffect(() => {
    if (!hydrated) return;
    try {
      const persistName = customName && customName.trim() ? customName : null;
      localStorage.setItem(
        IDENTITY_STORAGE_KEY,
        JSON.stringify({ customName: persistName, avatar, showIdentity }),
      );
    } catch {}
  }, [hydrated, customName, avatar, showIdentity]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const SIZE = 128;
        const canvas = document.createElement('canvas');
        canvas.width = SIZE;
        canvas.height = SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        // center-crop to a square, then downscale to 128px
        const min = Math.min(img.width, img.height);
        const sx = (img.width - min) / 2;
        const sy = (img.height - min) / 2;
        ctx.drawImage(img, sx, sy, min, min, 0, 0, SIZE, SIZE);
        setAvatar(canvas.toDataURL('image/jpeg', 0.82));
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  };

  const renderAvatar = (px: number) => {
    const common = {
      width: `${px}px`,
      height: `${px}px`,
      borderRadius: '9999px',
      flexShrink: 0,
      objectFit: 'cover' as const,
    };

    return avatar ? (
      <styled.img src={avatar} alt="" style={common} />
    ) : (
      <styled.div
        style={{ ...common, backgroundImage: getAddressGradient(address) }}
      />
    );
  };

  return {
    address,
    customName,
    setCustomName,
    avatar,
    showIdentity,
    setShowIdentity,
    defaultName,
    displayName,
    fileRef,
    onFileChange,
    renderAvatar,
  };
};

export type ShareIdentity = ReturnType<typeof useShareIdentity>;

/** Avatar + name as shown inside the share image. Caller positions it. */
export const ShareIdentityRow = ({
  identity,
  nameColor = 'white',
  ...rest
}: { identity: ShareIdentity; nameColor?: string } & HstackProps) => {
  if (!identity.showIdentity) return null;

  return (
    <HStack gap="8px" alignItems="center" {...rest}>
      {identity.renderAvatar(26)}
      <Text
        color={nameColor}
        fontWeight="semibold"
        fontSize="14px"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        maxW="220px"
      >
        {identity.displayName}
      </Text>
    </HStack>
  );
};

/**
 * Avatar + name combined with an optional referral code, shown below the PnL on
 * trade share images. Renders nothing when identity is hidden and no code.
 */
export const ShareIdentityProfile = ({
  identity,
  code,
  ...rest
}: { identity: ShareIdentity; code?: string } & HstackProps) => {
  const showName = identity.showIdentity;
  if (!showName && !code) return null;

  return (
    <HStack gap="10px" alignItems="center" {...rest}>
      {showName && identity.renderAvatar(34)}
      <VStack gap="2px" alignItems="flex-start">
        {showName && (
          <Text
            color="white"
            fontWeight="semibold"
            fontSize="15px"
            lineHeight="1.1"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxW="220px"
          >
            {identity.displayName}
          </Text>
        )}
        {code && (
          <HStack gap="6px" alignItems="baseline">
            <Text textStyle="text.xsmall" color="greyWhite.lightGrey4">
              Code
            </Text>
            <Text fontSize="16px" fontWeight="bold" color="white">
              {code}
            </Text>
          </HStack>
        )}
      </VStack>
    </HStack>
  );
};

/** "Your identity" controls (toggle + avatar upload + name input). */
export const ShareIdentityControls = ({
  identity,
}: {
  identity: ShareIdentity;
}) => {
  const {
    customName,
    setCustomName,
    defaultName,
    showIdentity,
    setShowIdentity,
    fileRef,
    onFileChange,
    renderAvatar,
  } = identity;

  return (
    <VStack px={6} alignItems="stretch" gap={2}>
      <HStack justifyContent="space-between" alignItems="center">
        <Text textStyle="headings.small" color="greyWhite.lightGrey3">
          Your identity
        </Text>
        <Checkbox
          indicatorOn={<Checkmark />}
          checked={showIdentity}
          onCheckedChange={(d) => setShowIdentity(d.checked === true)}
          css={{
            gap: '6px',
            '& [data-part="control"]': {
              width: '18px',
              height: '18px',
              p: '1',
            },
            '& [data-part="label"]': {
              textStyle: 'text.small',
              color: 'greyWhite.lightGrey3',
            },
          }}
        >
          Show identity
        </Checkbox>
      </HStack>

      {showIdentity && (
        <HStack gap={2} alignItems="center">
          <styled.button
            type="button"
            onClick={() => fileRef.current?.click()}
            title="Upload avatar"
            className="group"
            position="relative"
            flexShrink={0}
            cursor="pointer"
            borderRadius="9999px"
            overflow="hidden"
            style={{ width: '40px', height: '40px' }}
          >
            {renderAvatar(40)}
            <styled.span
              position="absolute"
              top={0}
              left={0}
              w="full"
              h="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="rgba(0,0,0,0.4)"
              opacity={0}
              transition="opacity 0.15s"
              _groupHover={{ opacity: 1 }}
            >
              <Icon boxSize="14px" color="white">
                <Edit />
              </Icon>
            </styled.span>
          </styled.button>

          <styled.input
            value={customName === null ? defaultName : customName}
            onFocus={() => {
              // clear the address prefill so users type a fresh name
              if (customName === null) setCustomName('');
            }}
            onChange={(e) => {
              const v = e.target.value;
              // block address-like input to prevent impersonation
              if (/^0x/i.test(v)) return;
              setCustomName(v);
            }}
            onBlur={() => {
              if (customName !== null && !customName.trim()) {
                setCustomName(null);
              }
            }}
            placeholder="Display name"
            maxLength={30}
            flex={1}
            h="40px"
            px="12px"
            borderRadius="8px"
            border="1px solid"
            borderColor="greyWhite.outline"
            bg="#19191e"
            color="greyWhite.lightGrey1"
            fontSize="14px"
            outline="none"
            _focusVisible={{
              borderColor: 'greyWhite.outline',
              outline: 'none',
            }}
          />

          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={onFileChange}
          />
        </HStack>
      )}
    </VStack>
  );
};
